// Tiny localStorage cache primitives.
//
// Two distinct use cases share this module:
//
// 1. `cacheGet` / `cacheSet` — TTL-based key-value cache, used by Open Food
//    Facts barcode + search lookups so repeat queries don't hit the network.
//    Stored under "cc.cache.<key>" with an expiry timestamp.
//
// 2. `loadWithCache` — read-through cache for the main data loads (meals,
//    goals, water, favorites) so an offline or slow-network user sees their
//    last-known data instead of a blank red error screen (UI audit Critical
//    #7). Scoped per Supabase user id so a shared device doesn't leak data
//    across accounts. Stored under "cc-cache-v1:<key>".

// ---------------------------------------------------------------------------
// (1) TTL cache — used by the OFF helpers
// ---------------------------------------------------------------------------

interface Entry<T> {
  v: T;
  exp: number;
}

const PREFIX = "cc.cache.";

export function cacheGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry<T>;
    if (e.exp && e.exp < Date.now()) {
      localStorage.removeItem(PREFIX + key);
      return null;
    }
    return e.v;
  } catch {
    return null;
  }
}

export function cacheSet<T>(key: string, value: T, ttlMs: number): void {
  try {
    const entry: Entry<T> = { v: value, exp: Date.now() + ttlMs };
    localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded etc — silently ignore. Cache is best-effort.
  }
}

export const DAY_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// (2) Read-through cache — used for meal / goal / water / favorite loads
// ---------------------------------------------------------------------------

const RT_PREFIX = "cc-cache-v1:";
const MAX_BYTES_PER_KEY = 1024 * 1024; // 1 MB hard cap

interface CachedPayload<T> {
  data: T;
  syncedAt: number; // epoch ms
  userId: string | null;
}

function readRTCached<T>(key: string, userId: string | null): CachedPayload<T> | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(RT_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload<T>;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.userId !== userId) return null; // stale account / shared device
    return parsed;
  } catch {
    return null;
  }
}

function writeRTCached<T>(key: string, userId: string | null, data: T): void {
  if (typeof localStorage === "undefined") return;
  try {
    const entry: CachedPayload<T> = { data, syncedAt: Date.now(), userId };
    const serialized = JSON.stringify(entry);
    if (serialized.length > MAX_BYTES_PER_KEY) {
      console.warn(`cache: refusing to cache ${key} (${serialized.length} bytes)`);
      return;
    }
    localStorage.setItem(RT_PREFIX + key, serialized);
  } catch (err) {
    console.warn(`cache: failed to write ${key}`, err);
  }
}

/** Drop every read-through cache entry — used on signOut + delete-account. */
export function clearAllReadThroughCache(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(RT_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export interface CachedLoadResult<T> {
  data: T;
  fromCache: boolean;
  /** When this `data` was last fetched from the server. */
  syncedAt: number;
  /** Present only when the live fetch failed and we fell back to cache. */
  staleError?: Error;
}

/**
 * Run `fetcher` and cache the result. If `fetcher` throws AND we have a
 * cached entry, return the cached entry instead (with `fromCache: true`
 * and the error in `staleError`). If there's no cache, the error propagates.
 */
export async function loadWithCache<T>(
  key: string,
  userId: string | null,
  fetcher: () => Promise<T>
): Promise<CachedLoadResult<T>> {
  try {
    const data = await fetcher();
    writeRTCached(key, userId, data);
    return { data, fromCache: false, syncedAt: Date.now() };
  } catch (err) {
    const cached = readRTCached<T>(key, userId);
    if (cached) {
      return {
        data: cached.data,
        fromCache: true,
        syncedAt: cached.syncedAt,
        staleError: err instanceof Error ? err : new Error(String(err))
      };
    }
    throw err;
  }
}

/** Compact "X min ago" for the offline pill. */
export function formatSyncedAt(ts: number): string {
  const ageMs = Date.now() - ts;
  const min = Math.floor(ageMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const d = Math.floor(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
